import React from 'react';
import { CalendarPlus, ChevronRight, ListChecks, SkipForward } from 'lucide-react';
import { anker } from '../../tutorial/anker';
import { ProjectLockHint } from '../ProjectLockHint';
import type { ProjectLock } from '../../services/projectLockLogic';
import { Agenda, AgendaItem, AgendaKind, DashboardProject, shortDayLabel } from './dashboardModel';
import { EmptyState, Panel, PanelHeader, Tag } from './dashboardUi';
import { DOT_TONE, Tone } from './dashboardTones';

interface Props {
    agenda: Agenda;
    locks: ReadonlyMap<string, ProjectLock>;
    lockNowMs: number;
    userId: string | null | undefined;
    onOpen: (project: DashboardProject) => void;
    onPostpone: (project: DashboardProject) => void;
    onSkip: (project: DashboardProject) => void;
    /** Handy: nur Ueberfaellig/Heute/Morgen offen, der Rest zugeklappt. */
    compact?: boolean;
    restOpen?: boolean;
    onToggleRest?: () => void;
    /** Gruppe, zu der gerade gerollt wurde — kurz hervorgehoben. */
    highlightKey?: string | null;
    ref?: React.Ref<HTMLDivElement>;
    groupRef?: (key: string, el: HTMLElement | null) => void;
}

const KIND_TONE: Record<AgendaKind, Tone> = { overdue: 'red', today: 'amber', tomorrow: 'blue', upcoming: 'slate' };

const dueText = (it: AgendaItem): string => {
    if (it.diff < 0) return `${Math.abs(it.diff)} Tag${Math.abs(it.diff) === 1 ? '' : 'e'} überfällig`;
    if (it.diff === 0) return 'Heute fällig';
    if (it.diff === 1) return 'Morgen fällig';
    return `Fällig ${shortDayLabel(it.due)}`;
};

/** Das Datumsfeld links: Tag und Monat, bei Ueberfaelligem die Zahl der Tage. */
const DateBox: React.FC<{ it: AgendaItem }> = ({ it }) => {
    const tone = KIND_TONE[it.kind];
    const box = it.kind === 'overdue' ? 'bg-red-600 text-white' : it.kind === 'today' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-700';
    const d = new Date(`${it.due}T12:00:00`);
    return (
        <div className={`w-11 h-11 rounded-lg flex flex-col items-center justify-center shrink-0 ${box}`} aria-hidden="true">
            {it.kind === 'overdue' ? (
                <>
                    <span className="text-sm font-semibold leading-none tabular">−{Math.abs(it.diff)}</span>
                    <span className="text-[11px] leading-none opacity-90 mt-0.5">Tage</span>
                </>
            ) : (
                <>
                    <span className="text-sm font-semibold leading-none tabular">{String(d.getDate()).padStart(2, '0')}</span>
                    <span className={`text-[11px] leading-none mt-0.5 ${tone === 'slate' ? 'text-slate-500' : 'opacity-90'}`}>{d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '')}</span>
                </>
            )}
        </div>
    );
};

const Row: React.FC<{
    it: AgendaItem;
    first: boolean;
    locks: ReadonlyMap<string, ProjectLock>;
    lockNowMs: number;
    userId: string | null | undefined;
    onOpen: (p: DashboardProject) => void;
    onPostpone: (p: DashboardProject) => void;
    onSkip: (p: DashboardProject) => void;
    compact: boolean;
}> = ({ it, first, locks, lockNowMs, userId, onOpen, onPostpone, onSkip, compact }) => (
    <li {...anker('dashboard.faellige-zeile', first)} className={`flex items-stretch gap-1 px-2 py-1 ${it.kind === 'overdue' ? 'bg-red-50/40' : ''}`}>
        <button
            type="button"
            onClick={() => onOpen(it.project)}
            className="flex-1 min-w-0 flex items-center gap-3 text-left rounded-lg px-1.5 py-1.5 hover:bg-slate-50 active:bg-slate-100 transition-colors"
        >
            <DateBox it={it} />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                    {it.number && <span className="text-[11px] font-mono text-slate-500 shrink-0">{it.number}</span>}
                    <span className={`text-xs font-semibold truncate ${it.kind === 'overdue' ? 'text-red-800' : 'text-slate-800'}`}>{it.name || '—'}</span>
                    {it.isSub && <Tag tone="teal" title="Vertretung / Vertreterliste">V</Tag>}
                    <ProjectLockHint lock={locks.get(it.id)} currentUserId={userId} nowMs={lockNowMs} compact />
                </div>
                {it.address && <p className="text-[11px] text-slate-500 truncate mt-0.5">{it.address}</p>}
                <p className={`text-[11px] mt-0.5 truncate ${it.kind === 'overdue' ? 'text-red-700 font-semibold' : it.kind === 'today' ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>
                    {dueText(it)} · {it.sifa ? 'SiFa' : 'Begehung'} · {it.rhythm}
                </p>
            </div>
            {compact && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
        </button>
        {!compact && (
            <div className="flex flex-col justify-center gap-1 shrink-0">
                <button
                    type="button"
                    onClick={() => onPostpone(it.project)}
                    title="Um 7 Tage verschieben"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-orange-700 px-2 py-1 rounded-md border border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-colors"
                >
                    <CalendarPlus className="w-3 h-3" /> +7d
                </button>
                {it.canSkip && (
                    <button
                        type="button"
                        onClick={() => onSkip(it.project)}
                        title="Diesen Termin überspringen (nächster laut Turnus)"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800 px-2 py-1 rounded-md border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                    >
                        <SkipForward className="w-3 h-3" /> Übersp.
                    </button>
                )}
            </div>
        )}
    </li>
);

/**
 * „Mein Plan": Ueberfaellig, Heute, Morgen, dann die naechsten Tage je
 * Wochentag. Jede Zeile ist ein echter Knopf (Tastatur), die Aktionen
 * daneben ebenfalls — nichts ist in etwas Klickbarem verschachtelt.
 */
export const AgendaPanel: React.FC<Props> = ({
    agenda, locks, lockNowMs, userId, onOpen, onPostpone, onSkip,
    compact = false, restOpen = true, onToggleRest, highlightKey, ref, groupRef,
}) => {
    const hasHot = agenda.overdue > 0 || agenda.today > 0;
    const groups = compact && !restOpen
        ? agenda.groups.filter(g => g.kind !== 'upcoming')
        : agenda.groups;
    const restCount = agenda.groups.filter(g => g.kind === 'upcoming').reduce((s, g) => s + g.items.length, 0);
    // Der Tutorial-Anker sitzt genau auf der ersten Zeile der Liste.
    const firstId = agenda.items[0]?.id;

    return (
        <Panel ref={ref} data-tutorial="dashboard.faellige-begehungen" className="scroll-mt-3">
            <PanelHeader
                icon={<ListChecks />}
                title="Mein Plan"
                count={agenda.items.length}
                countTone={agenda.overdue > 0 ? 'red' : agenda.today > 0 ? 'amber' : 'slate'}
                sub={hasHot ? `${agenda.overdue} überfällig · ${agenda.today} heute` : `nächste 7 Tage`}
            />
            {agenda.items.length === 0 ? (
                <EmptyState tone="ok" title="Alles erledigt" hint="Keine fälligen Begehungen in den nächsten 7 Tagen." className="py-8" />
            ) : (
                <div className="divide-y divide-slate-100">
                    {groups.map(g => {
                        const tone = KIND_TONE[g.kind];
                        const lit = highlightKey === g.key;
                        return (
                            <div key={g.key} ref={el => groupRef?.(g.key, el)} className={`scroll-mt-3 transition-colors ${lit ? 'bg-blue-50/50' : ''}`}>
                                <div className="px-3.5 pt-2 pb-1 flex items-center gap-2">
                                    <span className={`w-1.5 h-1.5 rounded-full ${DOT_TONE[tone]}`} aria-hidden="true" />
                                    <h3 className={`text-[11px] font-semibold uppercase tracking-wide ${tone === 'red' ? 'text-red-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-500'}`}>{g.label}</h3>
                                    <span className="text-[11px] text-slate-400 tabular">{g.items.length}</span>
                                </div>
                                <ul className="pb-1">
                                    {g.items.map(it => (
                                        <Row key={it.id} it={it} first={it.id === firstId} locks={locks} lockNowMs={lockNowMs} userId={userId}
                                            onOpen={onOpen} onPostpone={onPostpone} onSkip={onSkip} compact={compact} />
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                    {compact && restCount > 0 && onToggleRest && (
                        <button type="button" onClick={onToggleRest} className="w-full min-h-[44px] px-3.5 text-[11px] font-semibold text-blue-700 hover:bg-slate-50 flex items-center justify-center gap-1">
                            {restOpen ? 'Weitere Tage einklappen' : `Weitere ${restCount} in den nächsten 7 Tagen`}
                            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${restOpen ? '-rotate-90' : 'rotate-90'}`} />
                        </button>
                    )}
                </div>
            )}
        </Panel>
    );
};
